import { useState, type ReactNode } from 'react';
import { X, Lock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../../ui/Button';
import { cn } from '../../../utils/cn';
import { PAYMENT_STATE_CLASSES, PAYMENT_STATE_LABELS } from '../../../utils/nurseryMoney';
import type { PaymentState, XeroSyncStatus } from '../../../types/nurseryAccounts';

/**
 * Small pieces the nursery accounts pages share: the payment-state badge, the
 * Xero sync marker, a dialog shell, and a "give a reason" dialog for the
 * actions that are recorded with one (void, dispute, revoke).
 */

export function PaymentStateBadge({ state, className }: { state: PaymentState; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap', PAYMENT_STATE_CLASSES[state], className)}>
      {PAYMENT_STATE_LABELS[state]}
    </span>
  );
}

const XERO_LABEL: Record<Exclude<XeroSyncStatus, null>, string> = {
  pending: 'Waiting to go to Xero',
  pushing: 'Going to Xero',
  synced: 'In Xero',
  failed: 'Not in Xero',
};

/** Where a document stands with Xero. Renders nothing when it is not for Xero. */
export function XeroSyncBadge({ status, className }: { status: XeroSyncStatus | undefined; className?: string }) {
  if (!status) return null;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
      status === 'synced' ? 'bg-sky-50 text-sky-800' : status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-neutral-100 text-neutral-600',
      className)}>
      {XERO_LABEL[status]}
    </span>
  );
}

export interface DialogProps {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Buttons, right-aligned on a wide screen and stacked on a phone. */
  footer?: ReactNode;
  wide?: boolean;
}

/** A bottom sheet on a phone, a centred panel otherwise. */
export function Dialog({ title, subtitle, onClose, children, footer, wide }: DialogProps) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className={cn('bg-white w-full rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto', wide ? 'sm:max-w-2xl' : 'sm:max-w-lg')}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 break-words">{title}</h2>
            {subtitle && <p className="text-sm text-neutral-500 break-words">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">{children}</div>
        {footer && <div className="px-5 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export interface ReasonDialogProps {
  title: string;
  /** What will happen, in a sentence. */
  body: ReactNode;
  label: string;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  /** Throw to show the message and keep the dialog open. */
  onConfirm: (reason: string) => Promise<void>;
}

export function ReasonDialog({ title, body, label, confirmLabel, danger, onClose, onConfirm }: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = reason.trim().length >= 3;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={confirm} disabled={!ok || busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-neutral-700 break-words">{body}</div>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      <div>
        <label htmlFor="reason-text" className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
        <textarea id="reason-text" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
      </div>
    </Dialog>
  );
}

/** The screen a page shows someone without the seat it needs. */
export function SeatRequired({ seat, what }: { seat: 'user' | 'admin'; what: string }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
        <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-neutral-800 mb-1">
          {seat === 'admin' ? 'Accounts Admin licence required' : 'Accounts licence required'}
        </h2>
        <p className="text-sm text-neutral-500 mb-4 break-words">
          {what} needs an{' '}
          {seat === 'admin'
            ? <span className="font-medium">Accounts Admin</span>
            : <><span className="font-medium">Accounts User</span> or <span className="font-medium">Accounts Admin</span></>}{' '}
          licence seat. Your organisation&rsquo;s admin adds the Accounts module in the Module Store and assigns the seats.
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to the dashboard
        </Button>
      </div>
    </div>
  );
}

/** A tab strip that wraps on a phone rather than running off the edge. */
export function Tabs<T extends string>({ tabs, value, onChange, label }: {
  tabs: { key: T; label: string; count?: number }[];
  value: T;
  onChange: (key: T) => void;
  label: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key} onClick={() => onChange(t.key)}
          className={cn('rounded-lg border px-3 py-1.5 text-sm',
            value === t.key ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium' : 'bg-white border-neutral-300 text-neutral-600')}>
          {t.label}{t.count ? <span className="ml-1.5 text-xs text-neutral-500">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
